/**
 * parseProjeto — logica de parsing do projeto DinaBox -> itens da lista.
 *
 * Portado VERBATIM do HTML original "REVISOR DE LISTA DE FERRAGENS" (a parte
 * que a usuaria considera "perfeita"), apenas desacoplado da UI. Consome o JSON
 * cru do DinaBox (woodwork[].inputs[] + holes[]) que o backend repassa.
 *
 * Regras preservadas:
 * - Itens iguais (mesmo unique_id + dimensoes) somam SO dentro do mesmo modulo.
 * - holes[] viram um modulo virtual "Dispositivos de montagem" (ordem 9999).
 * - Categoria "Custos de execucao" nasce oculta da impressao.
 */

export const HOLES_MODULE_ID = "__holes__";
export const HOLES_MODULE_NAME = "Dispositivos de montagem";

/**
 * Arredonda quantidade para 3 casas (padrao do backend: Decimal 12,3).
 * OBRIGATORIO em toda SOMA e todo PAYLOAD de quantidade: somar floats em JS
 * gera ruido binario (3.35 + 2.69 = 6.039999999999999, 16 digitos) e o
 * backend rejeitava a lista inteira com "nao haja mais de 12 digitos".
 */
export function arredQt(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 1000) / 1000;
}

export const HIDDEN_CATEGORIES_BY_DEFAULT = new Set([
  "Custos de execução",
  "Custos de execucao",
]);

/** Categorias do DinaBox para o select de "Adicionar item manual". */
export const DINABOX_CATEGORIES = [
  "Acabamentos",
  "Acessórios",
  "Cola e adesivos",
  "Componentes elétricos",
  "Custos de execução",
  "Dispositivos de montagem",
  "Eletrodomésticos",
  "Ferragem",
  "Fitas de borda",
  "Frete e logística",
  "Iluminação",
  "Mão de obra",
  "Materiais de uso geral",
  "Painéis e chapas",
  "Parafusos",
  "Pedras e granitos",
  "Puxadores",
  "Tintas e vernizes",
  "Vidros e espelhos",
  "Outros",
];

/**
 * Constroi a lista de itens a partir do JSON cru do projeto DinaBox.
 * Agrupa por modulo; soma itens iguais dentro do mesmo modulo.
 */
export function buildItemsFromProject(proj) {
  const map = new Map();
  let order = 0;

  function addItem({
    key, moduleId, moduleName, moduleOrder, category, name,
    manufacturer, description, dimensions, qt, origin,
  }) {
    const fullKey = `${moduleId}::${key}`;
    const existing = map.get(fullKey);
    if (existing) {
      existing.qt = arredQt(existing.qt + qt);
    } else {
      map.set(fullKey, {
        id: "item_" + ++order,
        key: fullKey,
        moduleId,
        moduleName,
        moduleOrder,
        category: category || "Outros",
        name: name || "",
        manufacturer: manufacturer || "",
        description: description || "",
        dimensions: dimensions || "",
        qt: arredQt(qt),
        origin: origin || "api",
        hidden: HIDDEN_CATEGORIES_BY_DEFAULT.has(category),
        manual: false,
      });
    }
  }

  // 1) Inputs dos modulos (woodwork[])
  (proj.woodwork || []).forEach((mod, modIdx) => {
    const moduleId = mod.mid || mod.id || `mod_${modIdx}`;
    const moduleName = mod.name || "Módulo";
    (mod.inputs || []).forEach((inp) => {
      const key = `${inp.unique_id || inp.id}::${inp.dimensions || ""}`;
      addItem({
        key,
        moduleId,
        moduleName,
        moduleOrder: modIdx,
        category: inp.category_name,
        name: inp.name,
        manufacturer: inp.manufacturer || "",
        description: inp.description || "",
        dimensions: inp.dimensions || "",
        qt: Number(inp.qt) || 0,
        origin: "input",
      });
    });
  });

  // 2) Holes -> modulo virtual "Dispositivos de montagem"
  (proj.holes || []).forEach((h) => {
    const key = `hole::${h.id || h.ref}::${h.dimensions || ""}`;
    addItem({
      key,
      moduleId: HOLES_MODULE_ID,
      moduleName: HOLES_MODULE_NAME,
      moduleOrder: 9999,
      category: HOLES_MODULE_NAME,
      name: h.name,
      dimensions: (h.dimensions === "---" ? "" : h.dimensions) || "",
      qt: Number(h.qt) || 0,
      origin: "hole",
    });
  });

  return Array.from(map.values());
}

/**
 * Agrupa itens por moduleId, preservando a ordem do woodwork.
 * Retorna [{ moduleId, moduleName, moduleOrder, items: [...] }].
 */
export function groupByModule(items) {
  const groups = new Map();
  for (const it of items) {
    if (!groups.has(it.moduleId)) {
      groups.set(it.moduleId, {
        moduleId: it.moduleId,
        moduleName: it.moduleName,
        moduleOrder: it.moduleOrder,
        items: [],
      });
    }
    groups.get(it.moduleId).items.push(it);
  }
  return Array.from(groups.values()).sort((a, b) => a.moduleOrder - b.moduleOrder);
}

/** Normaliza texto p/ comparacao tolerante: minusculas, sem acento, espaco unico. */
export function normalizarTexto(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Chave de consolidacao do resumo, TOLERANTE a variacoes de digitacao manual:
 * ignora maiusculas/acentos/espacos e a POSICAO do fabricante (ora no nome, ora
 * no campo fabricante). NAO funde por similaridade — itens com palavras
 * diferentes (ex.: "reta amortecedor" vs "reta com amortecedor") seguem
 * separados (decisao: so normalizacao, sem fuzzy).
 */
export function chaveResumo(name, manufacturer, dimensions) {
  const n = normalizarTexto(name);
  const m = normalizarTexto(manufacturer);
  // Anexa o fabricante so se ele ainda nao estiver no nome (resolve o caso de
  // "HAFELE" estar ora no nome, ora no campo fabricante).
  const nomeChave = m && !n.includes(m) ? `${n} ${m}` : n;
  const d = normalizarTexto(dimensions).replace(/\s+/g, ""); // "35 mm" == "35mm"
  return `${nomeChave}__${d}`;
}

/**
 * Resumo consolidado: soma itens iguais atravessando TODOS os ambientes e
 * modulos. Ignora ocultos e qt<=0. A chave usa `chaveResumo` (normalizada) para
 * unir variacoes triviais de digitacao manual; o display mantem a 1a variacao.
 */
export function buildSummary(ambientes) {
  const map = new Map();
  ambientes.forEach((amb) => {
    amb.items.forEach((it) => {
      if (it.hidden || Number(it.qt) <= 0) return;
      const key = chaveResumo(it.name, it.manufacturer, it.dimensions);
      const existing = map.get(key);
      if (existing) {
        existing.qt = arredQt(existing.qt + it.qt);
      } else {
        map.set(key, {
          name: it.name,
          manufacturer: it.manufacturer || "",
          dimensions: it.dimensions || "",
          qt: it.qt,
        });
      }
    });
  });
  return Array.from(map.values()).sort((a, b) =>
    a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })
  );
}

/** Mapeia um item do editor para o payload de item da lista (backend). */
export function itemParaPayload(amb, item, ordem) {
  return {
    ordem,
    projeto_dinabox_id: String(amb.projectId || ""),
    ambiente_nome: amb.raw?.project_description || "",
    modulo_id: item.moduleId === HOLES_MODULE_ID ? "" : String(item.moduleId || ""),
    modulo_nome: item.moduleName || "",
    categoria: item.category || "",
    nome: item.name || "",
    fabricante: item.manufacturer || "",
    descricao: item.description || "",
    dimensoes: item.dimensions || "",
    quantidade: arredQt(item.qt),
    origem: item.origin || "api",
    oculto: Boolean(item.hidden),
    manual: Boolean(item.manual),
    // Conferencia feita ao vivo no editor (capturada ao finalizar).
    conferido: Boolean(item.conferido),
  };
}
